import type { RenDecorationLinkTargetDefinition, RenDecorationNavigationTarget } from '../../types'
import { normalizeSearch, normalizeUpperText } from './template-market'

export type LinkTargetCategory = 'all' | 'independent-page' | 'business-module' | 'business-page'

export const LINK_TARGET_CATEGORY_OPTIONS: ReadonlyArray<{ value: LinkTargetCategory; label: string }> = [
  { value: 'all', label: '全部' },
  { value: 'independent-page', label: '独立页面' },
  { value: 'business-module', label: '业务模块' },
  { value: 'business-page', label: '业务页面' },
]

const CATEGORY_LABELS: Record<Exclude<LinkTargetCategory, 'all'>, string> = {
  'independent-page': '独立页面',
  'business-module': '业务模块',
  'business-page': '业务页面',
}

export function pageCodeFromTabRoute(routePath: string): string {
  const matched = routePath.match(/[?&]pageCode=([^&]+)/)
  return matched ? decodeURIComponent(matched[1]).trim() : ''
}

export function isExplicitIndependentPageTarget(target: RenDecorationLinkTargetDefinition): boolean {
  if (normalizeSearch(target.moduleCode) !== 'decoration') return false
  const appShell =
    normalizeSearch(target.pageCode) === 'app' || normalizeUpperText(target.targetType) === 'TABBAR_SHELL'
  if (appShell) return false
  return (
    normalizeSearch(target.targetCode).startsWith('portal.app.') ||
    String(target.groupName ?? '')
      .trim()
      .includes('租户自定义页面')
  )
}

export function isLegacyIndependentPageTarget(target: RenDecorationLinkTargetDefinition): boolean {
  const pageCode = String(target.pageCode ?? '').trim()
  return (
    normalizeSearch(target.moduleCode) === 'decoration' &&
    normalizeUpperText(target.targetType) === 'PAGE' &&
    Boolean(pageCode) &&
    pageCodeFromTabRoute(target.routePath) === pageCode
  )
}

export function isIndependentPageTarget(target: RenDecorationLinkTargetDefinition): boolean {
  return isExplicitIndependentPageTarget(target) || isLegacyIndependentPageTarget(target)
}

export function isDynamicTabTarget(target: RenDecorationLinkTargetDefinition): boolean {
  if (isExplicitIndependentPageTarget(target)) return true
  if (normalizeSearch(target.moduleCode) !== 'decoration') return false
  return isLegacyIndependentPageTarget(target)
}

export function linkTargetCategory(target: RenDecorationLinkTargetDefinition): Exclude<LinkTargetCategory, 'all'> {
  if (isIndependentPageTarget(target)) return 'independent-page'
  if (normalizeUpperText(target.targetType) === 'PRODUCT_HOME') return 'business-module'
  return 'business-page'
}

export function linkTargetCategoryLabel(target: RenDecorationLinkTargetDefinition): string {
  return CATEGORY_LABELS[linkTargetCategory(target)]
}

export function linkTargetIdentity(
  target: Pick<RenDecorationLinkTargetDefinition, 'targetType' | 'moduleCode' | 'pageCode' | 'targetCode'>,
): string {
  return [target.targetType, target.moduleCode, target.pageCode, target.targetCode].join(':')
}

export function linkTargetForNavigation(
  targets: readonly RenDecorationLinkTargetDefinition[],
  navigation: Pick<RenDecorationNavigationTarget, 'targetType' | 'moduleCode' | 'pageCode' | 'targetCode'>,
): RenDecorationLinkTargetDefinition | null {
  const exact = targets.find(
    (target) =>
      target.targetType === navigation.targetType &&
      target.moduleCode === navigation.moduleCode &&
      target.pageCode === navigation.pageCode &&
      target.targetCode === navigation.targetCode,
  )
  if (exact || navigation.targetType === 'DECORATION_PAGE') return exact ?? null
  return targets.find((target) => target.targetCode === navigation.targetCode) ?? null
}

export function linkTargetByRoutePath(
  targets: readonly RenDecorationLinkTargetDefinition[],
  routePath: string,
): RenDecorationLinkTargetDefinition | null {
  return targets.find((target) => target.routePath === routePath) ?? null
}

export const MINI_PROGRAM_TARGET_CODE = 'decoration.mini-program.open'

export function miniProgramParamsError(params: Record<string, unknown>): string {
  if (!/^[A-Za-z0-9][A-Za-z0-9_-]{0,127}$/.test(String(params.appId ?? '').trim())) {
    return 'App ID 只能包含字母、数字、下划线和连字符，长度为 1 至 128 位'
  }
  for (const key of ['appName', 'path', 'query', 'version']) {
    if (params[key] != null && typeof params[key] !== 'string') return '小程序参数必须为文本'
    if (/[\u0000-\u001f\u007f]/.test(String(params[key] ?? ''))) return '小程序参数不能包含控制字符'
  }
  const path = String(params.path ?? '').trim()
  if (path.includes('://') || path.startsWith('//') || path.includes('\\') || path.includes('?') || path.includes('#') || path.split('/').some((part) => part === '..' || part === '.')) {
    return '页面路径必须为小程序内路径，查询参数请单独填写'
  }
  try {
    decodeURIComponent(String(params.query ?? '').replace(/\+/g, ' '))
  } catch {
    return '页面参数包含无效的 URL 编码'
  }
  return ''
}

export function miniProgramRoute(params: Record<string, string | number | boolean>): string {
  const query = new URLSearchParams()
  for (const key of ['appId', 'appName', 'path', 'query', 'version']) {
    const value = String(params[key] ?? '').trim()
    if (value) query.set(key, value)
  }
  return `/mini-program?${query.toString()}`
}

export function miniProgramParamsFromRoute(route: string): Record<string, string> | null {
  if (!route.startsWith('/mini-program?')) return null
  const query = new URLSearchParams(route.slice(route.indexOf('?') + 1))
  return Object.fromEntries(['appId', 'appName', 'path', 'query', 'version'].map((key) => [key, query.get(key) ?? '']))
}

export type LinkTargetApplyPayload = {
  target: RenDecorationLinkTargetDefinition
  params: Record<string, string | number | boolean>
  paramBindings: Record<string, string>
}
